import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataTable,
  InputText,
  Modal,
  SectionCard,
  Select,
  type ColumnDef,
  type SelectOption,
  PageWrapper,
} from "@/deps/ui-library";
import {
  useAccountMapping,
  useAccountMappingHistory,
  useReportFrameworks,
  useSaveAccountMapping,
  useSubscriptionStatus,
} from "@/api/queries";
import type { AccountMappingRow, ReportFramework } from "@/api/types";
import { useCan } from "@/app/session";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { SegmentControl } from "@/components/SegmentControl";
import { formatAmount } from "@/lib/money";
import { offered, tagLabel } from "../tagLabel";

/** The feature's code in RigoSub's catalogue (ADR-0061). */
const FEATURE = "REPORTING";
const LAST_FRAMEWORK = "rigo.reporting.lastFramework";

const STATUS = {
  unmapped: { label: "Unmapped", colorScheme: "warning" },
  suggested: { label: "Suggested", colorScheme: "warning" },
  confirmed: { label: "Confirmed", colorScheme: "success" },
  excluded: { label: "Excluded", colorScheme: "default" },
} as const;

type Filter = "review" | "all";

/** What's left to do: unconfirmed, and it matters, so active or holding a balance. */
const needsReview = (row: AccountMappingRow) =>
  (row.status === "suggested" || row.status === "unmapped") &&
  (row.isActive || Number(row.balance) !== 0);

function lastFramework() {
  try {
    return localStorage.getItem(LAST_FRAMEWORK);
  } catch {
    return null;
  }
}

function rememberFramework(code: string) {
  try {
    localStorage.setItem(LAST_FRAMEWORK, code);
  } catch {
    // A remembered tab is a convenience. Without storage the page opens on the first one.
  }
}

interface Values {
  choice: string;
  reason: string;
}

const EXCLUDE = "exclude";

const resolver: Resolver<Values> = (values) => {
  const errors: Record<string, { type: string; message: string }> = {};
  if (!values.choice) errors.choice = { type: "required", message: "Choose a tag." };
  if (values.choice === EXCLUDE && !values.reason.trim()) {
    errors.reason = { type: "required", message: "Say why it's left out." };
  }
  return { values: Object.keys(errors).length > 0 ? {} : values, errors };
};

const first = (value: SelectOption | SelectOption[] | null) =>
  String((Array.isArray(value) ? value[0] : value)?.value ?? "");

function SetTagDialog({
  framework,
  row,
  onClose,
}: {
  framework: ReportFramework;
  row: AccountMappingRow;
  onClose: () => void;
}) {
  const save = useSaveAccountMapping();
  const { data: history } = useAccountMappingHistory(framework.code, row.accountId);
  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<Values>({
    resolver,
    defaultValues: {
      choice: row.status === "excluded" ? EXCLUDE : (row.tagCode ?? ""),
      reason: row.exclusionReason ?? "",
    },
  });
  const excluding = watch("choice") === EXCLUDE;
  const tags = offered(framework.tags, row.tagCode).filter((tag) => tag.group === row.group);

  return (
    <Modal.Form
      open
      title={`${row.code} ${row.name}`}
      subtitle={
        row.suggestedBecause
          ? `Suggested because the name contains "${row.suggestedBecause}".`
          : undefined
      }
      submitLabel="Save"
      busy={save.isPending}
      error={save.error ? (save.error as Error).message : null}
      onClose={onClose}
      onSubmit={() =>
        void handleSubmit(({ choice, reason }) =>
          save.mutate(
            {
              framework: framework.code,
              decisions: [
                choice === EXCLUDE
                  ? { accountId: row.accountId, tagCode: null, exclusionReason: reason }
                  : { accountId: row.accountId, tagCode: choice, exclusionReason: null },
              ],
            },
            { onSuccess: onClose },
          ),
        )()
      }
    >
      <div className="flex flex-col gap-3">
        <Controller
          control={control}
          name="choice"
          render={({ field }) => (
            <Select
              required
              label="Tag"
              options={[
                ...tags.map((tag) => ({ value: tag.code, label: tagLabel(tag, framework.tags) })),
                { value: EXCLUDE, label: "Leave out of this framework" },
              ]}
              value={field.value}
              showClearButton={false}
              errorText={errors.choice?.message}
              onChange={(details) => field.onChange(first(details.value))}
            />
          )}
        />
        {excluding ? (
          <InputText
            required
            label="Reason"
            errorText={errors.reason?.message}
            {...register("reason")}
          />
        ) : null}
        {history && history.length > 0 ? (
          <div className="flex flex-col gap-1">
            <span className="text-fg-muted text-xs">Changes</span>
            <ul className="flex flex-col gap-1 text-xs">
              {history.map((change) => (
                <li key={change.changedAt} className="text-fg">
                  {change.changedAt.slice(0, 10)}: {change.oldTagCode ?? "None"} to{" "}
                  {change.newTagCode ?? "Left out"}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Modal.Form>
  );
}

function Workspace({ frameworks }: { frameworks: ReportFramework[] }) {
  const can = useCan();
  const editable = can("reports.layout.edit");
  const [code, setCode] = useState(() => {
    const last = lastFramework();
    return frameworks.some((f) => f.code === last) ? last! : frameworks[0]!.code;
  });
  const framework = frameworks.find((f) => f.code === code)!;
  const { data, isPending, error } = useAccountMapping(code);
  const save = useSaveAccountMapping();
  const [filter, setFilter] = useState<Filter>("review");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<AccountMappingRow | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    rememberFramework(code);
    setSelected(new Set());
  }, [code]);

  const rows = useMemo(() => data ?? [], [data]);
  const review = rows.filter(needsReview);
  const confirmed = rows.filter((r) => r.status === "confirmed").length;
  const suggested = rows.filter((r) => r.status === "suggested").length;
  const excluded = rows.filter((r) => r.status === "excluded").length;
  const complete = rows.length > 0 && review.length === 0;

  const text = search.trim().toLowerCase();
  const shown = (filter === "review" ? review : rows).filter(
    (r) => !text || `${r.code} ${r.name}`.toLowerCase().includes(text),
  );

  // Only what's been read as a suggestion can be accepted: a confirmation is a person's own.
  const acceptable = shown.filter((r) => r.status === "suggested");
  const chosen = acceptable.filter((r) => selected.has(r.accountId));

  const toggle = (id: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const columns = useMemo<ColumnDef<AccountMappingRow>[]>(() => {
    const label = (tag: string | null) => {
      const found = framework.tags.find((t) => t.code === tag);
      return found ? tagLabel(found, framework.tags) : (tag ?? "");
    };
    return [
      ...(editable
        ? [
            {
              id: "select",
              header: "",
              cell: ({ row: { original } }: { row: { original: AccountMappingRow } }) =>
                original.status === "suggested" ? (
                  <Checkbox
                    aria-label={`Choose ${original.name}`}
                    checked={selected.has(original.accountId)}
                    onCheckedChange={(details: { checked: boolean | "indeterminate" }) =>
                      toggle(original.accountId, details.checked === true)
                    }
                  />
                ) : null,
            },
          ]
        : []),
      { id: "code", header: "Code", cell: ({ row }) => row.original.code },
      { id: "name", header: "Account", cell: ({ row }) => row.original.name },
      { id: "group", header: "Type", cell: ({ row }) => row.original.group },
      {
        id: "balance",
        header: "Balance",
        meta: { numeric: true },
        cell: ({ row }) => formatAmount(row.original.balance),
      },
      {
        id: "tag",
        header: "Tag",
        cell: ({ row: { original } }) =>
          original.status === "excluded" ? original.exclusionReason : label(original.tagCode),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row: { original } }) => (
          <Badge colorScheme={STATUS[original.status].colorScheme} size="sm">
            {STATUS[original.status].label}
          </Badge>
        ),
      },
      ...(editable
        ? [
            {
              id: "set",
              header: "",
              meta: { textAlign: "right" as const },
              cell: ({ row: { original } }: { row: { original: AccountMappingRow } }) => (
                <Button
                  colorScheme="default"
                  variant="ghost"
                  size="xs"
                  aria-label={`Change ${original.name}`}
                  onClick={() => setEditing(original)}
                >
                  Change
                </Button>
              ),
            },
          ]
        : []),
    ];
  }, [editable, selected, framework]);

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title="Account mapping"
        subtitle={`${confirmed} of ${rows.length} accounts confirmed · ${suggested} suggested · ${excluded} excluded`}
      />

      <div className="flex flex-wrap items-center gap-3">
        <SegmentControl
          label="Framework"
          segments={frameworks.map((f) => ({ value: f.code, label: f.name }))}
          value={code}
          onChange={setCode}
        />
        <SegmentControl
          label="Show"
          segments={[
            { value: "review", label: "Needs review", badge: review.length },
            { value: "all", label: "All", badge: rows.length },
          ]}
          value={filter}
          onChange={setFilter}
        />
        <div className="w-64">
          <InputText
            aria-label="Search accounts"
            placeholder="Search code or account"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(error as Error).message}
        />
      ) : null}
      {complete ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "success" }}
          description="Complete. Every account is confirmed or excluded."
        />
      ) : null}
      {save.error && !editing ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(save.error as Error).message}
        />
      ) : null}

      {editable && acceptable.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setSelected(
                new Set(
                  chosen.length === acceptable.length ? [] : acceptable.map((row) => row.accountId),
                ),
              )
            }
          >
            {chosen.length === acceptable.length ? "Clear" : "Choose all suggested"}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={chosen.length === 0 || save.isPending}
            onClick={() =>
              save.mutate(
                {
                  framework: code,
                  decisions: chosen.map((row) => ({
                    accountId: row.accountId,
                    tagCode: row.tagCode,
                    exclusionReason: null,
                  })),
                },
                { onSuccess: () => setSelected(new Set()) },
              )
            }
          >
            {`Accept ${chosen.length} suggested`}
          </Button>
        </div>
      ) : null}

      <SectionCard title="Accounts" flush>
        {isPending ? (
          <p className="text-fg-muted p-4 text-sm">Loading</p>
        ) : shown.length === 0 ? (
          <p className="text-fg-muted p-4 text-sm">
            {filter === "review" && !text
              ? "Nothing needs review in this framework."
              : "No account matches this search."}
          </p>
        ) : (
          <DataTable
            aria-label="Account mapping"
            size="sm"
            enableSorting={false}
            columns={columns}
            data={shown}
            getRowId={(row) => row.accountId}
            pagination={{ mode: "manual", show: false }}
          />
        )}
      </SectionCard>

      <p className="text-fg-muted text-xs">Supplied format · not a compliance assertion</p>

      {editing ? (
        <SetTagDialog framework={framework} row={editing} onClose={() => setEditing(null)} />
      ) : null}
    </PageWrapper>
  );
}

/** One job: get every account to confirmed or excluded (ADR-0061). */
export function ReportMappingPage() {
  const subscription = useSubscriptionStatus();
  const frameworks = useReportFrameworks();
  const features = subscription.data?.features;

  // RigoSub says who has this. No list is no instruction, so nothing's held back.
  if (features && !features.includes(FEATURE)) {
    return (
      <EmptyState
        icon="Lock"
        title="Account mapping comes with the Reporting layer add-on"
        description="Statements in the standard formats stay available. An owner or admin can add it to the subscription."
      />
    );
  }

  if (frameworks.isPending) return <p className="text-fg-muted p-6 text-sm">Loading</p>;
  if (frameworks.error || !frameworks.data?.length) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        description={
          frameworks.error ? (frameworks.error as Error).message : "No frameworks are available."
        }
      />
    );
  }

  return <Workspace frameworks={frameworks.data} />;
}
